import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Por favor introduza o seu email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Email de recuperação enviado!');
    } catch (error) {
      console.error('Error requesting password reset:', error);
      // Always show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <ShieldCheck className="w-12 h-12 text-[#10B981]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Privaxia
          </h1>
          <p className="text-sm text-slate-600">Plataforma de Gestão de Consentimentos RGPD</p>
        </div>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#10B981]" />
              Recuperar Password
            </CardTitle>
            <CardDescription>
              Introduza o seu email para receber instruções de recuperação
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">Email Enviado!</h3>
                  <p className="text-sm text-slate-600 mt-2">
                    Se o email <strong>{email}</strong> estiver registado na nossa plataforma, 
                    receberá instruções para recuperar a sua password.
                  </p>
                </div>
                <div className="pt-4 space-y-3">
                  <p className="text-xs text-slate-500">
                    Não recebeu o email? Verifique a pasta de spam ou tente novamente.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSubmitted(false)}
                    className="w-full"
                  >
                    Tentar novamente
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="forgot-password-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    required
                  />
                </div>
                
                <Button
                  data-testid="forgot-password-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#10B981] hover:bg-[#10B981]/90"
                >
                  {loading ? 'A enviar...' : 'Enviar Instruções'}
                </Button>
              </form>
            )}
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <Link
                to="/login-local"
                className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-[#10B981] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao Login
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 Privaxia. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
